/* eslint-disable react-refresh/only-export-components -- co-localizamos
   `classifyMatchLevel` con el componente para que los tests lo usen. */
/**
 * Badge de relevancia del matching (Sprint 4).
 *
 * Mapea el `score` (0-100) del motor de matching a 4 niveles visuales:
 *
 * - **Alta** (≥ 75): verde, "Recomendada · 87%". El alcalde debería ver esto primero.
 * - **Media** (≥ 50): azul brand, "Relevante · 62%". Vale la pena revisar.
 * - **Baja** (≥ 25): slate, "Poco alineada · 35%".
 * - **Muy baja** (< 25): gris claro, "Filtrar con cuidado · 10%".
 * - **Sin señales** (`noSignals`, issue #541): ninguna regla del motor
 *   aplicó y el score es el piso de 30 que recibe toda convocatoria activa.
 *   Dice "Sin priorizar" y no muestra porcentaje: "Poco alineada · 30%"
 *   sería un juicio que el motor no hizo.
 *
 * Usa `data-match-level` para que los tests puedan aseverar nivel sin parsear texto.
 * Lógica pura en `classifyMatchLevel`.
 */

export type MatchLevel = "high" | "medium" | "low" | "very_low";

const HIGH_THRESHOLD = 75;
const MEDIUM_THRESHOLD = 50;
const LOW_THRESHOLD = 25;

export function classifyMatchLevel(score: number): MatchLevel {
  if (score >= HIGH_THRESHOLD) return "high";
  if (score >= MEDIUM_THRESHOLD) return "medium";
  if (score >= LOW_THRESHOLD) return "low";
  return "very_low";
}

const LEVEL_LABELS: Record<MatchLevel, string> = {
  high: "Recomendada",
  medium: "Relevante",
  low: "Poco alineada",
  very_low: "Revisar con cuidado",
};

const LEVEL_CLASSES: Record<MatchLevel, string> = {
  high: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  medium: "bg-brand-50 text-brand-700 ring-brand-200",
  low: "bg-slate-100 text-slate-700 ring-slate-200",
  very_low: "bg-slate-50 text-slate-500 ring-slate-200",
};

export interface MatchBadgeProps {
  score: number;
  /** El motor no encontró ninguna razón (`match.reasons` vacío). */
  noSignals?: boolean;
  className?: string;
}

export function MatchBadge({ score, noSignals = false, className }: MatchBadgeProps) {
  const base =
    "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset";
  if (noSignals) {
    return (
      <span
        data-testid="match-badge"
        data-match-level="none"
        title="Ningún criterio de Kaptar (PDET, región, sector o experiencia en SGR) conecta esta convocatoria con tu municipio."
        className={`${base} bg-white text-slate-600 ring-slate-300 ${className ?? ""}`}
      >
        Sin priorizar
      </span>
    );
  }
  const level = classifyMatchLevel(score);
  return (
    <span
      data-testid="match-badge"
      data-match-level={level}
      className={`${base} ${LEVEL_CLASSES[level]} ${className ?? ""}`}
    >
      {LEVEL_LABELS[level]}
      <span className="font-mono text-[10px] opacity-80">{score}%</span>
    </span>
  );
}
